import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Home, BookOpen, Compass, User, Plus, LayoutGrid } from 'lucide-react';

interface TabItem {
  icon: React.ReactNode;
  activeIcon?: React.ReactNode;
  label: string;
  path: string;
  isMain?: boolean;
}

interface BottomTabBarProps {
  tabs?: TabItem[];
  currentPath: string;
  className?: string;
  onTabChange?: (path: string) => void;
}

export const BottomTabBar: React.FC<BottomTabBarProps> = ({
  tabs,
  currentPath,
  className,
  onTabChange,
}) => {
  const navigate = useNavigate();

  // Default tabs if none provided
  const defaultTabs: TabItem[] = [
    {
      icon: <Home className="h-6 w-6" />,
      activeIcon: <Home className="h-6 w-6 fill-current" />,
      label: "Trang chủ",
      path: "/",
    },
    {
      icon: <LayoutGrid className="h-6 w-6" />,
      activeIcon: <LayoutGrid className="h-6 w-6 fill-current" />,
      label: "Sơ đồ",
      path: "/diagrams",
    },
    {
      icon: <Plus className="h-6 w-6" />,
      activeIcon: <Plus className="h-6 w-6" />,
      label: "",
      path: "/chat",
      isMain: true,
    },
    {
      icon: <BookOpen className="h-6 w-6" />,
      activeIcon: <BookOpen className="h-6 w-6 fill-current" />,
      label: "Học tập",
      path: "/courses/category/phat-hoc",
    },
    {
      icon: <User className="h-6 w-6" />,
      activeIcon: <User className="h-6 w-6 fill-current" />,
      label: "Tôi",
      path: "/profile",
    },
  ];

  const tabsToRender = tabs || defaultTabs;

  const isActive = (path: string) => {
    if (path === "/" && currentPath === "/") return true;
    if (path !== "/" && currentPath.startsWith(path)) return true;
    return false;
  };

  const handleTabClick = (path: string) => {
    if (onTabChange) {
      onTabChange(path);
    } else {
      navigate(path);
    }
  };

  return (
    <div className={cn(
      "w-full flex items-center justify-between px-1 py-1 bg-background",
      className
    )}>
      {tabsToRender.map((tab, index) => {
        const active = isActive(tab.path);
        
        // Special styling for main tab
        if (tab.isMain) {
          return (
            <div 
              key={index}
              className="flex-1 flex justify-center"
              onClick={() => handleTabClick(tab.path)}
            >
              <div className="relative flex flex-col items-center justify-center">
                {/* Main action button with elevated effect */}
                <div className="flex items-center justify-center w-12 h-12 bg-primary rounded-full shadow-lg mb-1 hover:bg-primary/90 transition-colors touch-ripple">
                  <div className="text-primary-foreground">
                    {active && tab.activeIcon ? tab.activeIcon : tab.icon}
                  </div>
                </div>
                {tab.label && (
                  <span className="text-[10px] font-medium">
                    {tab.label}
                  </span>
                )}
              </div>
            </div>
          );
        }
        
        // Regular tab
        return (
          <div 
            key={index}
            className={cn(
              "flex-1 flex flex-col items-center justify-center py-2 cursor-pointer",
              active ? "text-primary" : "text-muted-foreground"
            )}
            onClick={() => handleTabClick(tab.path)}
          >
            <div className={cn(
              "relative mb-1",
              active && "nav-tab-active"
            )}>
              {active && tab.activeIcon ? tab.activeIcon : tab.icon}
            </div>
            <span className="text-[10px] font-medium">{tab.label}</span>
          </div>
        );
      })}
    </div>
  );
};

export default BottomTabBar; 